import Translator from "./translator";
import { DriveStore } from "../store/reducers";
import { UserService } from "./users";

export let minDate = new Date(Date.UTC(1986, 0, 1)); //"1986-01-01T00:00:00Z"
export let maxDate = new Date(Date.UTC(2050, 0, 1)); //2050 jan 1

const M_TO_FT = 3.28084;
const MI_TO_M = 1609.344;
const KM_TO_M = 1000;

interface ILocalUnits {
    longDistance: string;
    shortDistance: string;
}

interface ICanadianDvirMeasurements {
    hubometer?: number;
    loadHeight?: number;
    loadWidth?: number;
}

export class LocalizerService {
    private localUnits: ILocalUnits | undefined;

    constructor(_store: DriveStore, private readonly userService: UserService) {
    }

    /**
     * Returns a string of distance in localized units and suffix (e.g. 50 returns "5km")
     * @param distance In meters
     * @param isLong if true, return distance in long distance units (ie km, mi), otherwise in short distance units (m, ft). isLong will be rounded up to the nearest whole number
     */
    toLocalDistanceString(distance: number, isLong = true) {
        let localDistance = this.toLocalDistance(distance, isLong);
        return `${ isLong ? Math.ceil(localDistance) : localDistance.toFixed(2) } ${ this.getDistanceUnit(isLong) }`;
    }

    /**
     * Returns a double of distance in localized units (e.g. 50 returns 5)
     * @param distance In meters
     * @param isLong if true, return distance in long distance units (ie km, mi), otherwise in short distance units (m, ft)
     */
    toLocalDistance(metreDistance: number, isLong = true) {
        let currentUser = this.userService.getCurrentUser();
        if (currentUser) {
            if (isLong) {
                return currentUser.isMetric ? metreDistance / 1000 : metreDistance / MI_TO_M;
            }
            return currentUser.isMetric ? metreDistance : metreDistance * M_TO_FT;
        }
        return metreDistance;
    }

    toMetresFromLocalDistance(localDistance: number, isLong = true) {
        let currentUser = this.userService.getCurrentUser();
        if (currentUser) {
            if (isLong) {
                return currentUser.isMetric ? localDistance * KM_TO_M : localDistance * MI_TO_M;
            }
            return currentUser.isMetric ? localDistance : localDistance / M_TO_FT;
        }
        return localDistance;
    }

    /**
     * Returns ts the formal name for the language
     * @param language The shortcode for the language
     */
    getLanguageName(language: string): string {
        let languages = {
            "en": "English",
            "fr": "French (Canada)",
            "es": "Spanish (Latam)",
            "es-419": "Spanish (Latam)",
            "ja": "Japanese",
            "de": "German",
            "pl": "Polish",
            "pt-BR": "Brazilian-Portuguese",
            "nl": "Dutch",
            "it": "Italian",
            "zh-Hans": "简体中文",
            "th": "ไทย",
            "id": "Bahasa Indonesia",
            "cs": "Čeština",
            "sv": "Svenska",
            "tr": "Türkçe",
            "ms": "Bahasa Malay",
            "fr-FR": "Français (France)",
            "ko-KR": "한국어",
            "zh-TW": "繁體中文",
            "da-DK": "Dansk",
            "fi-FI": "Suomi",
            "hu-HU": "Magyar",
            "nb-NO": "Norwegian Bokmål",
            "sk-SK": "Slovenčina",
            "pt-PT": "Português (Portugal)",
            "ro-RO": "Română",
            "es-ES": "Spanish (Spain)",
            "bg-BG": "Български",
            "el-GR": "Ελληνικά",
            "et-EE": "Eesti keel",
            "ka-GE": "ქართული",
            "lt-LT": "Lietuvių kalba",
            "lv-LV": "Latviešu valoda",
            "ar-001": "العربية"
        };
        const normalizedLanguage = typeof language === "string" ? language.toLowerCase() : language;
        const displayLanguage = normalizedLanguage === "es-419" ? "es-419" : normalizedLanguage === "es-es" ? "es-ES" : language;
        return languages[displayLanguage] || language;
    }

    initUserUnits(translator: Translator, language?: string) {
        return translator.translate(this.getLocalDistance(), language)
            .then(([longDistance, shortDistance]) => {
                this.localUnits = {
                    longDistance,
                    shortDistance
                };
            });
    }

    getDistanceUnit(isLong: boolean) {
        let units = this.localUnits;
        if (!units) {
            let localDistance = this.getLocalDistance();
            units = {
                longDistance: localDistance[0],
                shortDistance: localDistance[1]
            };
        }
        return isLong ? units.longDistance : units.shortDistance;
    }

    convertStringValuesToNumericMetricForCanadianFields(hubometer: string, loadWidth: string, loadHeight: string, isMetric = true): ICanadianDvirMeasurements {
        let updatedHubometer = hubometer ? parseFloat(hubometer) : undefined;
        let updatedLoadWidth = loadWidth ? parseFloat(loadWidth) : undefined;
        let updatedLoadHeight = loadHeight ? parseFloat(loadHeight) : undefined;

        if (isMetric) {
            updatedHubometer = updatedHubometer ? updatedHubometer * KM_TO_M : updatedHubometer;
        } else {
            updatedHubometer = updatedHubometer ? updatedHubometer * MI_TO_M : updatedHubometer;
            updatedLoadWidth = updatedLoadWidth ? updatedLoadWidth / M_TO_FT : updatedLoadWidth;
            updatedLoadHeight = updatedLoadHeight ? updatedLoadHeight / M_TO_FT : updatedLoadHeight;
        }

        return {
            hubometer: updatedHubometer,
            loadWidth: updatedLoadWidth,
            loadHeight: updatedLoadHeight
        };
    }

    getLongDistanceUnits(isMetric = true) {
        return isMetric ? "km" : "mi";
    }

    getShortDistanceUnits(isMetric = true) {
        return isMetric ? "m" : "ft";
    }

    private getLocalDistance() {
        let currentUser = this.userService.getCurrentUser();
        if (currentUser) {
            return currentUser.isMetric ? ["km", "m"] : ["mi", "ft"];
        }
        return ["m", "m"];
    }
}

